import React, { useState, useRef, useEffect } from "react";
import ReactQuill from "react-quill";
import "../../../../../../App.css";
import "react-quill/dist/quill.snow.css";
import { MessageSquare, Trash2, SquarePen } from "lucide-react";
import {
  Button,
  Text,
  useDisclosure,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  Spinner,
  Center,
  useToast,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import {
  addNotes,
  updateNotes,
  deleteNotes,
} from "../../../../../redux/api/admin/NotesApi";
import {
  addNoteAction,
  updateNoteAction,
  deleteNoteAction,
} from "../../../../../redux/reduxSlice/admin/NotesSlice";
import { getNotesList } from "../../../../../redux/reduxApi/admin/NotesReduxApi";
import { getLeadRelatedNotes } from "../../../../../redux/api/admin/leadApi";
import { getDealRelatedNotes } from "../../../../../redux/api/admin/dealApi";
import { getClientRelatedNotes } from "../../../../../redux/api/admin/clientApi";
import { getCompanyRelatedNotes } from "../../../../../redux/api/admin/companyApi";

export default function Notes({ moduleId, moduleName }) {
  // moduleId: the ID of the lead/deal/contact/company
  // moduleName: "lead", "deal", "contact", or "company"
  console.log("moduleName", moduleName);
  
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteText, setEditNoteText] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputError, setInputError] = useState(""); // For validation errors

  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();
  const dispatch = useDispatch();
  const toast = useToast();

  // Get notes from Redux store
  const getNotesData = useSelector((state) => state?.notes?.notes);

  const modules = {
    toolbar: [["bold", "italic", "underline"]],
  };

  // Fetch notes on component mount
  useEffect(() => {
    fetchNotes();
  }, [moduleId, moduleName]);

  // Update local state when Redux store changes
  useEffect(() => {
    if (getNotesData && getNotesData.length > 0) {
      setNotes(getNotesData);
    }
  }, [getNotesData]);

  // Select the appropriate API function based on moduleName
  const getApiFunction = () => {
    switch (moduleName) {
      case "lead":
        return getLeadRelatedNotes;
      case "deal":
        return getDealRelatedNotes;
      case "contact":
        return getClientRelatedNotes;
      case "company":
        return getCompanyRelatedNotes;
      default:
        return getLeadRelatedNotes; // fallback
    }
  };

  // Prepare the request data based on moduleName
  const getRequestData = () => {
    switch (moduleName) {
      case "lead":
        return { leadId: moduleId };
      case "deal":
        return { dealId: moduleId };
      case "contact":
        return { contactId: moduleId };
      case "company":
        return { companyId: moduleId };
      default:
        return { leadId: moduleId }; // fallback
    }
  };

  // Validate note content (remove HTML tags and check if it's empty)
  const validateNoteContent = (content) => {
    // Remove HTML tags and trim whitespace
    const textContent = content.replace(/<[^>]*>/g, "").trim();
    return textContent.length > 0;
  };

  // Show validation error
  const showValidationError = (message) => {
    setInputError(message);
    toast({
      title: message,
      status: "error",
      duration: 3000,
      isClosable: true,
    });
  };

  // Clear validation error
  const clearValidationError = () => {
    setInputError("");
  };

  // Fetch notes from API
  const fetchNotes = async () => {
    setLoading(true);
    try {
      const apiFunction = getApiFunction();
      const requestData = getRequestData();
      
      const response = await apiFunction(requestData);
      
      if (response.data?.success) {
        setNotes(response.data.tasks || []);
      }
    } catch (error) {
      console.error("Error fetching notes:", error);
      // toast({
      //   title: "Error fetching notes",
      //   status: "error",
      //   duration: 3000,
      //   isClosable: true,
      // });
    } finally {
      setLoading(false);
    }
  };

  // Add a new note
  const handleAddNote = async () => {
    // Validate note content
    if (!validateNoteContent(newNote)) {
      showValidationError("Note content cannot be empty");
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);
    clearValidationError();

    try {
      const noteData = {
        notes: newNote,
        lead: moduleName === "lead" ? moduleId : null,
        deal: moduleName === "deal" ? moduleId : null,
        contact: moduleName === "contact" ? moduleId : null,
        company: moduleName === "company" ? moduleId : null,
      };

      const response = await addNotes(noteData);
      
      if (response.data.success) {
        dispatch(addNoteAction(response.data.note));
        
        // Add note to local state
        const note = {
          id: response.data.note.ROWID || Date.now(),
          text: newNote,
          user: "You",
          time: new Date().toLocaleString(),
          ...response.data.note,
        };
        
        setNotes([note, ...notes]);
        setNewNote("");
        
        toast({
          title: "Note created successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error adding note:", error);
      toast({
        title: "Error creating note",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Start editing a note
  const handleEditNote = (note) => {
    setEditingNoteId(note.id || note.ROWID);
    setEditNoteText(note.text || note.notes);
    clearValidationError();
  };

  // Update a note
  const handleUpdateNote = async (noteId) => {
    // Validate note content
    if (!validateNoteContent(editNoteText)) {
      showValidationError("Note content cannot be empty");
      return;
    }

    if (isSubmitting) return;

    setIsSubmitting(true);
    clearValidationError();

    try {
      const updateData = {
        notes: editNoteText,
        lead: moduleName === "lead" ? moduleId : null,
        deal: moduleName === "deal" ? moduleId : null,
        contact: moduleName === "contact" ? moduleId : null,
        company: moduleName === "company" ? moduleId : null,
      };

      const response = await updateNotes(noteId, updateData);
      
      if (response.data.success) {
        dispatch(updateNoteAction({ id: noteId, ...updateData }));
        
        // Update note in local state
        setNotes(
          notes.map((note) =>
            (note.id || note.ROWID) === noteId
              ? { 
                  ...note, 
                  text: editNoteText,
                  notes: editNoteText,
                  time: new Date().toLocaleString() 
                }
              : note
          )
        );
        
        setEditingNoteId(null);
        setEditNoteText("");
        
        toast({
          title: "Note updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error("Error updating note:", error);
      toast({
        title: "Error updating note",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingNoteId(null);
    setEditNoteText("");
    clearValidationError();
  };

  // Delete a note
  const handleDelete = async () => {
    if (!deleteId) return;

    setIsSubmitting(true);

    try {
      await deleteNotes(deleteId);
      
      dispatch(deleteNoteAction([deleteId]));
      
      setNotes(notes.filter((note) => (note.id || note.ROWID) !== deleteId));
      
      toast({
        title: "Note deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting note:", error);
      toast({
        title: "Error deleting note",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setDeleteId(null);
      setIsSubmitting(false);
      onClose();
    }
  };

  function formatDateTime(dateInput) {
    // Convert to a Date object (in case you pass a string)
    const date = new Date(dateInput);

    // Extract parts
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    // Return formatted string
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  return (
    <div className="h-[79vh] overflow-y-auto my-12 mx-3 max-w-4xl mb-3">
      {/* Note Input */}
      <div className="rounded-2xl shadow-md bg-white/70 backdrop-blur-sm border border-gray-200 overflow-hidden mb-4 transition">
        <div className="border-0 rounded-md overflow-hidden">
          <ReactQuill
            theme="snow"
            value={newNote}
            onChange={(value) => {
              setNewNote(value);
              clearValidationError();
            }}
            placeholder="Write Your Notes Here..."
            style={{ 
              backgroundColor: "white", 
              minHeight: "60px",
              border: inputError && !validateNoteContent(newNote) ? "1px solid #e53e3e" : "none"
            }}
            modules={modules}
          />
        </div>

        {inputError && !validateNoteContent(newNote) && (
          <div className="px-3 py-1">
            <Text color="red.500" fontSize="sm">{inputError}</Text>
          </div>
        )}

        <div className="flex justify-start px-3 py-2 bg-gray-50">
          <button
            onClick={handleAddNote}
            disabled={isSubmitting || !validateNoteContent(newNote)}
            className="px-4 py-1 main-btn rounded-xl font-semibold hover:scale-105 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSubmitting ? "Adding..." : "+ Add Note"}
          </button>
        </div>
      </div>

      {/* All Notes */}
      <div className="space-y-4 overflow-y-auto mx-3 mb-5">
        <div className="flex items-center gap-2 border-bottom">
          <h1 className="font-semibold text-2xl">All Notes</h1>
          <div className="h-6 w-6 flex items-center justify-center rounded-full border-2 border-black ms-2 text-black">
            {notes.length}
          </div>
        </div>

        {loading ? (
          <Center height="200px">
            <Spinner size="xl" color="blue.500" thickness="4px" />
          </Center>
        ) : notes.length === 0 ? (
          <Center height="200px">
            <Text color="gray.500">No notes yet. Create your first note!</Text>
          </Center>
        ) : (
          notes.map((note) => {
            const noteId = note.id || note.ROWID;
            const isEditing = editingNoteId === noteId;

            return (
              <div key={noteId} className="flex flex-row w-full">
                <div className="h-9 w-9 flex items-center justify-center rounded-full border-2 ms-2 text-black">
                  <MessageSquare color="#1877F2" size="25px" strokeWidth="1" />
                </div>
                <div className="ps-3 rounded-md relative" style={{ width: "100%" }}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex flex-row gap-2">
                      <Text fontWeight="bold" fontSize="sm" color="blue.700" mb={0}>
                        {note.user || "You"}
                      </Text>
                      <Text fontSize="xs" color="gray.500" mb={0}>
                        {formatDateTime(note.MODIFIEDTIME || new Date())}
                      </Text>
                    </div>
                  </div>

                  {isEditing ? (
                    <div className="bg-white border rounded-md p-2">
                      <ReactQuill
                        theme="snow"
                        value={editNoteText}
                        onChange={(value) => {
                          setEditNoteText(value);
                          clearValidationError();
                        }}
                        placeholder="Edit your note..."
                        style={{ 
                          backgroundColor: "white", 
                          minHeight: "60px",
                          border: inputError && !validateNoteContent(editNoteText) ? "1px solid #e53e3e" : "none"
                        }}
                        modules={modules}
                      />
                      {inputError && !validateNoteContent(editNoteText) && (
                        <div className="mt-1">
                          <Text color="red.500" fontSize="sm">{inputError}</Text>
                        </div>
                      )}
                      <div className="flex gap-2 mt-2">
                        <button
                          onClick={() => handleUpdateNote(noteId)}
                          disabled={isSubmitting || !validateNoteContent(editNoteText)}
                          className="px-3 py-1 main-btn rounded-lg text-sm disabled:opacity-50"
                        >
                          {isSubmitting ? "Saving..." : "Save"}
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          disabled={isSubmitting}
                          className="px-3 py-1 cancel-button rounded-lg text-sm"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between bg-gray-100 p-1 rounded-md">
                      <div
                        className="text-md note-content p-1 flex-1"
                        dangerouslySetInnerHTML={{ __html: note.text || note.notes }}
                      />
                      <div className="flex gap-2">
                        <button onClick={() => handleEditNote(note)}>
                          <SquarePen color="#1877F2" size="22px" strokeWidth="1" />
                        </button>
                        <button
                          onClick={() => {
                            setDeleteId(noteId);
                            onOpen();
                          }}
                        >
                          <Trash2 color="#1877F2" size="22px" strokeWidth="1" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => {
          setDeleteId(null);
          onClose();
        }}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>Delete Note</AlertDialogHeader>
            <AlertDialogBody>
              Are you sure you want to delete this note?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button 
                ref={cancelRef} 
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                onClick={handleDelete}
                ml={3}
                disabled={isSubmitting}
                isLoading={isSubmitting}
              >
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </div>
  );
}